import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, FileType2, Upload, AlertTriangle, CheckCircle2, Loader2, X, Wand2, Save as SaveIcon } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { useApp } from '../context/AppContext';
import { formatCurrency } from '../lib/format';
import api from '../lib/api';

const humanSize = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * AI Import — upload a broker statement PDF, let Claude extract holdings,
 * preview them, then commit (insert / replace / append) into the active
 * source's grid via `onCommit({ rows, mode })`.
 */
export default function AiImportDialog({
  open, onOpenChange,
  snapshotId, sourceId, sourceName, snapshotLabel,
  existingRowCount = 0,
  onCommit,
}) {
  const { sources, updateSource } = useApp();
  const source = sources.find((s) => s.id === sourceId);
  const savedInstructions = source?.ai_instructions || '';

  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingInstr, setSavingInstr] = useState(false);
  const [result, setResult] = useState(null);   // { rows, warnings, model, pageCount, filename }
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('replace');
  const [instructions, setInstructions] = useState(savedInstructions);

  // Re-sync the textarea whenever the dialog (re)opens or the source changes
  useEffect(() => {
    if (open) setInstructions(savedInstructions);
  }, [open, sourceId, savedInstructions]);

  const instructionsDirty = (instructions || '').trim() !== (savedInstructions || '').trim();

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    setLoading(false);
    setMode('replace');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = (next) => {
    if (loading) return;   // don't close mid-request
    onOpenChange(next);
    if (!next) reset();
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    setError(null);
    setResult(null);
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) {
      setError('Only .pdf files are supported in this version.');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError('PDF is larger than 10 MB.');
      return;
    }
    setFile(f);
  };

  const runImport = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const out = await api.aiImport(snapshotId, sourceId, file, instructions);
      setResult(out);
      if ((out.rows || []).length === 0) {
        setError('AI could not find any holdings in this PDF. Try a different statement, refine the instructions, or use Paste / manual entry.');
      } else {
        toast.success(`AI extracted ${out.rows.length} row${out.rows.length === 1 ? '' : 's'} from ${out.filename}`);
      }
    } catch (e) {
      setError(e.message || 'AI import failed');
    } finally {
      setLoading(false);
    }
  };

  const saveInstructions = async () => {
    if (!source) return;
    setSavingInstr(true);
    try {
      await updateSource(sourceId, { ai_instructions: instructions });
      toast.success(`Instructions saved for ${sourceName}`);
    } catch (e) {
      toast.error(e.message || 'Could not save instructions');
    } finally {
      setSavingInstr(false);
    }
  };

  const commit = () => {
    if (!result?.rows?.length) return;
    onCommit({ rows: result.rows, mode });
    handleClose(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-3xl max-h-[88vh] overflow-y-auto"
        data-testid="ai-import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <Sparkles size={16} className="text-[hsl(var(--primary))]" /> AI Import — broker statement
          </DialogTitle>
          <DialogDescription>
            Upload a PDF statement for <span className="font-semibold">{sourceName}</span> · <span className="font-semibold">{snapshotLabel}</span>.
            Claude will extract holdings and pre-fill the grid. You'll review before anything is saved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Source-specific AI instructions */}
          {!result && (
            <div className="border border-border bg-[#FAFAF7] p-3 space-y-2" data-testid="ai-instructions-panel">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="ai-instructions" className="overline flex items-center gap-1.5">
                  <Wand2 size={11} /> Extraction instructions for {sourceName}
                </Label>
                {instructionsDirty && (
                  <span className="chip chip-warning text-[9px]" title="You have unsaved changes — they'll be used for this import only unless you click Save">
                    unsaved
                  </span>
                )}
              </div>
              <Textarea
                id="ai-instructions"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder={`e.g.\n• Look for the "Holdings" or "Portfolio Summary" section only — ignore "Transactions"\n• Map "Avg. Buy Cost" to cost and "Current Value" to marketValue\n• If a row says "MF" treat sub-asset as Mutual Fund\n• Skip cash/bank balance rows`}
                rows={5}
                className="text-[12px] font-mono leading-snug"
                data-testid="ai-instructions-textarea"
              />
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] text-[#787872] font-mono leading-snug">
                  These instructions are sent to Claude alongside every import for <span className="font-semibold">{sourceName}</span>.
                  Save to make them stick across sessions.
                </div>
                <button
                  className="btn-secondary !py-1 !px-2.5 text-[11px] disabled:opacity-50"
                  onClick={saveInstructions}
                  disabled={!instructionsDirty || savingInstr}
                  data-testid="ai-instructions-save"
                  title="Persist these instructions on the source so they're auto-loaded next time"
                >
                  {savingInstr ? <Loader2 size={11} className="animate-spin" /> : <SaveIcon size={11} />}
                  Save instructions
                </button>
              </div>
            </div>
          )}

          {/* File picker */}
          {!result && (
            <div className="border-2 border-dashed border-border rounded-sm p-6 text-center bg-[#FAFAF7]">
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={onPick}
                className="hidden"
                data-testid="ai-import-file-input"
              />
              {!file ? (
                <>
                  <FileType2 size={32} className="mx-auto text-[#787872] mb-2" />
                  <div className="font-heading font-semibold text-[14px]">Drop a PDF here, or pick a file</div>
                  <div className="text-[11px] text-[#787872] font-mono mt-1">PDF only · max 10 MB · text-based PDFs work best</div>
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="btn-primary mt-4 mx-auto"
                    data-testid="ai-import-pick-file"
                  >
                    <Upload size={13} /> Choose PDF
                  </button>
                </>
              ) : (
                <div className="flex items-center gap-3 justify-center">
                  <div className="w-10 h-10 border border-border flex items-center justify-center bg-white">
                    <FileType2 size={18} className="text-[#9B2C2C]" />
                  </div>
                  <div className="text-left">
                    <div className="font-heading font-semibold text-[13px]">{file.name}</div>
                    <div className="text-[11px] text-[#787872] font-mono">{humanSize(file.size)}</div>
                  </div>
                  <button onClick={() => setFile(null)} className="btn-ghost ml-2" title="Remove">
                    <X size={13} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Error banner */}
          {error && (
            <div className="border border-[#E3C3C3] bg-[#FDF2F2] p-3 text-[12px] text-[#7A1F1F] flex items-start gap-2" data-testid="ai-import-error">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <div>{error}</div>
            </div>
          )}

          {/* Loading state */}
          {loading && (
            <div className="border border-border bg-[#F1F6F2] p-4 text-[12px] flex items-center gap-3" data-testid="ai-import-loading">
              <Loader2 size={16} className="animate-spin text-[hsl(var(--primary))]" />
              <div>
                <div className="font-heading font-semibold text-[13px]">Reading the statement…</div>
                <div className="text-[#575757] font-mono">Claude Sonnet 4.5 is extracting holdings — usually 5-30 seconds.</div>
              </div>
            </div>
          )}

          {/* Preview */}
          {result?.rows?.length > 0 && (
            <div className="space-y-3" data-testid="ai-import-preview">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-[hsl(var(--positive))]" />
                  <span className="font-heading font-semibold text-[13px]">
                    {result.rows.length} row{result.rows.length === 1 ? '' : 's'} extracted
                  </span>
                  <span className="overline">· {result.pageCount} page{result.pageCount === 1 ? '' : 's'} · {result.model}</span>
                </div>
              </div>

              <div className="max-h-[280px] overflow-auto border border-border">
                <table className="grid-table text-[11px]">
                  <thead>
                    <tr>
                      <th>Institution</th>
                      <th>Holder</th>
                      <th>Investment</th>
                      <th>Asset Class</th>
                      <th>Sub Class</th>
                      <th>Strategy</th>
                      <th>Geo</th>
                      <th className="num">Cost</th>
                      <th className="num">Mkt Val</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((r) => (
                      <tr key={r.id}>
                        <td>{r.institution || <span className="text-[#AAA]">—</span>}</td>
                        <td>{r.holder || <span className="text-[#AAA]">—</span>}</td>
                        <td className="font-heading font-semibold">{r.investmentName || <span className="text-[#AAA]">—</span>}</td>
                        <td>{r.mainAssetClass || <span className="text-[#AAA]">—</span>}</td>
                        <td>{r.subAssetClass || <span className="text-[#AAA]">—</span>}</td>
                        <td>{r.marketCap || <span className="text-[#AAA]">—</span>}</td>
                        <td>{r.geography || <span className="text-[#AAA]">—</span>}</td>
                        <td className="num font-mono tabular-nums">{formatCurrency(r.cost, 'INR', { mode: 'full' })}</td>
                        <td className="num font-mono tabular-nums">{formatCurrency(r.marketValue, 'INR', { mode: 'full' })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {result.warnings?.length > 0 && (
                <div className="border border-[#E5D9B5] bg-[#FDF8E8] p-3 text-[11px] font-mono space-y-1" data-testid="ai-import-warnings">
                  <div className="font-heading font-semibold text-[12px] text-[#7A5A1F] flex items-center gap-1.5 mb-1">
                    <AlertTriangle size={12} /> {result.warnings.length} mapping warning{result.warnings.length === 1 ? '' : 's'}
                  </div>
                  {result.warnings.slice(0, 6).map((w, i) => (
                    <div key={i} className="text-[#7A5A1F]">· {w}</div>
                  ))}
                  {result.warnings.length > 6 && (
                    <div className="text-[#787872]">+ {result.warnings.length - 6} more — full list in Activity Log</div>
                  )}
                </div>
              )}

              <div>
                <Label className="overline mb-2 block">Import mode</Label>
                <RadioGroup value={mode} onValueChange={setMode} className="gap-2" data-testid="ai-import-mode">
                  <div className="flex items-start gap-2 py-1.5">
                    <RadioGroupItem value="replace" id="ai-mode-replace" className="mt-0.5" />
                    <label htmlFor="ai-mode-replace" className="text-[12px] cursor-pointer">
                      <div className="font-heading font-semibold">Replace</div>
                      <div className="text-[#575757]">Wipe the {existingRowCount} existing row{existingRowCount === 1 ? '' : 's'} for {sourceName} and load these instead.</div>
                    </label>
                  </div>
                  <div className="flex items-start gap-2 py-1.5">
                    <RadioGroupItem value="append" id="ai-mode-append" className="mt-0.5" />
                    <label htmlFor="ai-mode-append" className="text-[12px] cursor-pointer">
                      <div className="font-heading font-semibold">Append</div>
                      <div className="text-[#575757]">Add these as additional rows below the existing {existingRowCount} row{existingRowCount === 1 ? '' : 's'}.</div>
                    </label>
                  </div>
                </RadioGroup>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <button className="btn-secondary" onClick={() => handleClose(false)} disabled={loading}>
            Cancel
          </button>
          {!result && (
            <button
              className="btn-primary disabled:opacity-50"
              onClick={runImport}
              disabled={!file || loading}
              data-testid="ai-import-run"
            >
              {loading ? <><Loader2 size={13} className="animate-spin" /> Reading…</> : <><Sparkles size={13} /> Read with AI</>}
            </button>
          )}
          {result?.rows?.length > 0 && (
            <button
              className="btn-primary"
              onClick={commit}
              data-testid="ai-import-commit"
            >
              <CheckCircle2 size={13} /> Insert {result.rows.length} row{result.rows.length === 1 ? '' : 's'}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
